// SpessaSynth behind the SF2Synth interface — the worklet-engine spike.
//
// SF2Synth builds a WebAudio node graph per voice on the main thread (sixteen-odd
// nodes a note, scheduled through AudioParam timelines), which is where dense MIDI on
// a large font lags. This class keeps the same surface — ChannelData's handlers plus
// what the player calls — but every call becomes a few MIDI bytes stamped with the
// context time and posted to one AudioWorkletNode running spessasynth_core
// (spessa-processor.civet). The main thread allocates nothing per note.
//
// State replay on seek works unchanged: the player marks `preparing` while it
// re-dispatches events from the top of the song, notes are skipped like SF2Synth
// skips them, and the controller messages land in the worklet stamped in the past,
// so its queue drains them in order before the first real quantum.
//
// Not carried over, deliberately: the hardware-measured GS constants and the
// reverb/chorus/delay buses (docs/engine-accuracy.md). spessasynth applies its own
// GS/XG SysEx handling and its own effects; this spike is for measuring the lag,
// and the A/B/C listening kit is the judge of the rest.

type { MidiChannel } from ../midi/channel-data.civet
{ urlSafeSHA256 } from ../util.civet
{ readSoundBankName, readSoundBankPresets } from ./soundbank-format.civet

{ max, min, round } := Math

type SpessaEvent = { time: number, bytes: number[] }

type PreparedContext = BaseAudioContext & { spessaPrepared?: Promise<void>, spessaReady?: boolean }

/**
Per-channel outputs of the worklet: its 16 MIDI channels' dry voices, and the synth's own
reverb/chorus/delay return. Each tap feeds `out` unless something re-routes it — the
player's effect graph (composer/source/lib/effect-graph.civet) takes a channel's tap
through that channel's chain instead.
*/
export type SynthTaps =
  channels: GainNode[]
  effects: GainNode
  out: GainNode

type Entry =
  context: BaseAudioContext
  /** What `bind` returns — exists before the worklet module has registered. */
  out: GainNode
  taps: SynthTaps
  node: AudioWorkletNode?
  /** Last program sent per channel; a note on a channel whose program changed silently (state restore) resends it. */
  program: Int16Array
  pending: SpessaEvent[]
  flushScheduled: boolean

export class SpessaSynth
  /**
  Where the worklet processor (lib/dist/spessa-processor.js, ~440 KB with spessasynth_core
  inside) is served from. Unlike the reverb and dB worklets it is not inlined as a string:
  nothing loads it unless this engine is selected. The app sets it to the file it serves
  beside itself; the tests point it at the built file.
  */
  static processorUrl: string | URL | undefined

  /**
  Linear master gain handed to the processor. spessasynth scales its output by a fixed
  0.6 (SPESSASYNTH_GAIN_FACTOR); the default cancels it so levels match SF2Synth, which
  was measured 3–5 dB louder before this on every A/B song (docs/synth-engine-spike.md).
  */
  gain = 1 / 0.6
  /** Reverb, chorus and delay inside the worklet. Off for dry reference renders. */
  effectsEnabled = true

  /** The file as loaded: SF2, SF3 or DLS (`soundBankFormat` tells which). */
  buffer: ArrayBuffer?
  name: string?
  sha: string?
  /**
  The loaded bank's presets by bank then program, each with its `name` — SF2Synth's
  `banks` shape, which the channel settings list instruments from. Names only: the
  worklet holds the samples.
  */
  banks: { name: string }[][] = []
  /** Set by the player while it replays state on seek: notes are dropped, controllers pass. */
  preparing = false
  channels: MidiChannel[] = []
  context: BaseAudioContext?
  entries = new Map<BaseAudioContext, Entry>()
  entry: Entry?

  @(buffer?: ArrayBuffer)
    @load buffer if buffer

  prepareContext(context: PreparedContext)
    return context.spessaPrepared if context.spessaPrepared
    url := SpessaSynth.processorUrl
    unless url
      return Promise.reject new Error "SpessaSynth.processorUrl is not set: point it at the served spessa-processor.js"
    context.spessaPrepared = context.audioWorklet.addModule(url)
    .then ->
      context.spessaReady = true
      return
    .catch (e) ->
      context.spessaPrepared = undefined
      throw e

  load(source: string | ArrayBuffer, _options?: { attenuationDbPerUnit?: number })
    if source !<? ArrayBuffer
      source = fetch(source) |> await |> .arrayBuffer() |> await
    @buffer = source
    @name = readSoundBankName source
    banks: { name: string }[][] := []
    for { bank, program, name } of readSoundBankPresets source
      (banks[bank] ?= [])[program] ?= { name }
    @banks = banks
    @sha = await urlSafeSHA256 @toBlob()
    for entry of @entries.values()
      @sendBank entry
    return this

  sendBank(entry: Entry)
    return unless @buffer and entry.node
    // Structured clone, not a transfer: the worklet parses its own copy, ours stays
    // for toBlob. The parse runs on the render thread, so load before playing.
    entry.node.port.postMessage { type: "load", buffer: @buffer }

  /**
  Returns the node to connect. The worklet node itself is created once the module has
  registered on this context; until then events queue here, stamped, and drain in order.
  */
  bind(channels: MidiChannel[], context: BaseAudioContext)
    @channels = channels
    @context = context
    entry .= @entries.get context
    if entry
      // A re-bind restarts playback from a new position: silence what was sounding.
      // The player's state replay re-sends every controller and program after this.
      entry.program.fill -1
      entry.node?.port.postMessage { type: "stop", force: true }
    else
      out := context.createGain()
      taps: SynthTaps := {
        channels: [0...16].map =>
          g := context.createGain()
          g.connect out
          g
        effects: context.createGain()
        out
      }
      taps.effects.connect out
      created: Entry := {
        context
        out
        taps
        node: undefined
        program: new Int16Array(16).fill(-1)
        pending: []
        flushScheduled: false
      }
      entry = created
      @entries.set context, created
      attach := =>
        return unless @entries.get(context) is created
        // 16 channel pairs + the effects return (spessa-processor.civet's split mode).
        node := new AudioWorkletNode context, "spessa",
          numberOfInputs: 0
          numberOfOutputs: 17
          outputChannelCount: Array(17).fill 2
          processorOptions: { gain: @gain, effectsEnabled: @effectsEnabled }
        for tap, i of taps.channels
          node.connect tap, i
        node.connect taps.effects, 16
        created.node = node
        @sendBank created
        @flush created
      // Once the module is registered on this context the node can exist right away, so
      // events go straight to the port rather than through the pending queue.
      if (context as PreparedContext).spessaReady
        attach()
      else
        @prepareContext(context).then attach
    @entry = entry
    entry.out.disconnect()
    entry.out

  /** The per-channel taps on `context` (the bound one by default), or undefined before `bind`. */
  taps(context = @context): SynthTaps?
    @entries.get(context!)?.taps if context

  post(bytes: number[], t: number)
    entry := @entry
    return unless entry
    // Negative times sync song time to the context and mean "now"; the worklet drains
    // everything at or before its clock at the start of a quantum.
    entry.pending.push { time: max(0, t), bytes }
    unless entry.flushScheduled
      entry.flushScheduled = true
      queueMicrotask => @flush entry

  flush(entry: Entry)
    entry.flushScheduled = false
    return unless entry.node and entry.pending.length
    events := entry.pending
    entry.pending = []
    entry.node.port.postMessage { type: "midi", events }

  now()
    @context?.currentTime ?? 0

  noteOn(channel: MidiChannel, n: number, v: number, t: number)
    return if @preparing
    c := channel.id
    // A program the channel holds that was never sent (set without an event, as the
    // virtual keyboard does). Never re-send one that was: spessasynth resets the GS
    // drum NRPN parameters on every program change, so an extra one before a note
    // wipes them.
    if @entry and @entry.program[c] is -1 and channel.program !== 0
      @programChange channel, channel.program, t
    @post [0x90 | c, n & 0x7f, v & 0x7f], t

  noteOff(channel: MidiChannel, n: number, v: number, t: number)
    return if @preparing
    @post [0x80 | channel.id, n & 0x7f, v & 0x7f], t

  controlChange(channel: MidiChannel, n: number, v: number, t: number)
    @post [0xB0 | channel.id, n & 0x7f, v & 0x7f], t

  programChange(channel: MidiChannel, program: number, t: number)
    if @entry
      @entry.program[channel.id] = program
    @post [0xC0 | channel.id, program & 0x7f], t

  /** `bend` is the float in [-1, 1) ChannelData hands out; back to the 14-bit wire value. */
  pitchBend(channel: MidiChannel, bend: number, t: number)
    v := min 16383, max 0, round (bend + 1) * 8192
    @post [0xE0 | channel.id, v & 0x7f, v >> 7], t

  // RPN bend range and tuning reach the worklet as the CC 100/101/6/38 messages that
  // set them — spessasynth decodes RPNs itself.
  pitchBendSensitivity(_channel: MidiChannel, _cents: number, _t: number)
  channelTuning(_channel: MidiChannel, _cents: number, _t: number)

  allNotesOff(channel: MidiChannel, t: number)
    @post [0xB0 | channel.id, 123, 0], t

  allSoundsOff(channel?: MidiChannel, t?: number)
    t ?= @now()
    if channel
      @post [0xB0 | channel.id, 120, 0], t
    else
      // The player's stop: every channel, and the voices cut outright rather than through
      // CC 120's short release. The reverb tail inside the worklet still rings out.
      for c of [0...16]
        @post [0xB0 | c, 120, 0], t
      @entry?.node?.port.postMessage { type: "stop", force: true }

  /** Polyphonic key pressure — a per-voice modulator source in spessasynth. */
  keyPressure(channel: MidiChannel, n: number, v: number, t: number)
    @post [0xA0 | channel.id, n & 0x7f, v & 0x7f], t

  /** Channel pressure — a modulator source; the SF2 default set maps it to vibrato depth. */
  channelPressure(channel: MidiChannel, v: number, t: number)
    @post [0xD0 | channel.id, v & 0x7f], t

  /** Raw file SysEx, `[0xF0, <varint length>, payload…]` — the payload ends with F7. */
  sysEx(data: Uint8Array, t: number)
    i .= 1
    while data[i] & 0x80
      i++
    @post [0xF0, ...data.subarray(i + 1)], t

  // GS effect-block parameters arrive here already decoded by ChannelData; spessasynth
  // reads the SysEx itself (see `sysEx`), so there is nothing to apply.
  fxParam(_name: string, _value: number)

  toBlob()
    new Blob [@buffer!], type: "application/octet-stream"

  debugStatus()
    `SpessaSynth ${@name ?? "(no font)"} bound to ${@entries.size} context(s)`
